'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

/** 활성 옵션이 없는 상태 — `aria-activedescendant` 를 비우고 하이라이트 링도 그리지 않는다 */
export const NO_ACTIVE_OPTION = -1;

/**
 * 카테고리 리스트박스의 열림·활성 옵션·키보드 이동을 관리한다.
 *
 * 열리면 **리스트박스 자신이 실제 DOM 포커스**를 받는다(트리거가 아니다) — `aria-activedescendant`
 * 는 포커스를 든 요소에 달아야 하고, 옵션 자체는 포커스를 가질 필요가 없어진다(옵션은
 * `role="option"` 인 비포커스 요소). 데스크톱 드롭다운과 모바일 바텀시트가 이 모델 하나를
 * 공유한다 — 각자 다른 위치(앵커 드롭다운 vs vaul 포털)에 렌더되지만, 핸들러는 렌더된 그
 * 요소 자신에게 걸리므로 포털을 거슬러 올라오는 이벤트 버블링에 좌우되지 않는다.
 */
export function useCategoryListbox({
  selectedIndex,
  optionCount,
}: {
  selectedIndex: number;
  optionCount: number;
}) {
  const [open, setOpen] = useState(false);
  /**
   * 한 번이라도 열린 적이 있는지. 오버레이를 지연 로드하는 호출부가 "첫 오픈 전까지는
   * 렌더 자체를 안 한다"를 판단하는 데 쓴다 — 닫힌 뒤에도 계속 true 다.
   */
  const [hasOpened, setHasOpened] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(selectedIndex);
  /**
   * 하이라이트 링을 그릴지 — 키보드로 이동했을 때만 켠다.
   *
   * ⚠️ 활성 옵션 유무만으로 링을 그리면 안 된다. **터치 탭은 `mouseenter` 를 발생시켜서**
   * (모바일 브라우저 공통) 항목을 탭하는 순간 그 행에 링(`#040435`, 거의 검정)이 켜지고,
   * 시트가 닫히는 애니메이션 동안 그게 그대로 보인다.
   */
  const [keyboardActive, setKeyboardActive] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  /**
   * 리스트박스 DOM 노드가 실제로 붙는 순간 포커스를 준다.
   *
   * ⚠️ `useEffect` 안에서 `requestAnimationFrame` 으로 포커스를 주는 흔한 방식으로 바꾸지
   * 말 것 — **모바일 시트에서 조용히 실패한다.** 시트는 `next/dynamic` 청크라 파일을 받아
   * 렌더될 때까지 열림 상태 변경보다 수백 ms 늦을 수 있는데, `requestAnimationFrame` 은
   * **다음 프레임(약 16ms)에 딱 한 번 실행되고 끝나는** 예약이라 재시도가 없다. 그 시점에
   * 노드가 없으면 `null?.focus()` 가 되어 에러도 안 나고 포커스만 안 들어간다.
   *
   * 콜백 ref 는 노드가 화면에 붙는 그 순간 React 가 호출하므로 지연이 얼마든 맞는다.
   * `open` 을 의존성으로 메모이즈하는 건 재오픈 때문이다 — 모바일은 2번째부터 같은 노드를
   * 재사용해서, 함수 identity 가 그대로면 React 가 다시 호출하지 않는다. `open` 이 바뀌면
   * 새 함수가 되어 다시 불리므로 별도 재포커스 effect 가 필요 없다.
   */
  const setListboxNode = useCallback(
    (node: HTMLUListElement | null) => {
      listboxRef.current = node;
      if (node && open) node.focus();
    },
    [open],
  );

  /**
   * `viaKeyboard` 가 활성 옵션 표시(하이라이트 링)를 켤지 정한다.
   *
   * 포인터로 열면 활성 옵션을 두지 않는다(`-1`) — 두면 모바일에서 시트가 열리는 순간
   * 선택된 행에 링(`#040435`, 거의 검정)이 그려져 탭한 적도 없는 곳이 강조된다.
   * 키보드로 열면 지금 어디에 있는지 보여야 하므로 선택된 항목을 활성으로 둔다
   * (`:focus-visible` 이 포인터 포커스를 걸러 내는 것과 같은 기준).
   */
  const openListbox = (viaKeyboard: boolean) => {
    setHasOpened(true);
    setHighlightedIndex(viaKeyboard ? selectedIndex : NO_ACTIVE_OPTION);
    setKeyboardActive(viaKeyboard);
    setOpen(true);
  };

  /** 마우스 hover 로 활성 옵션을 옮긴다 — 링은 켜지 않는다(hover 배경으로만 표시) */
  const highlightByPointer = (index: number) => {
    setHighlightedIndex(index);
    setKeyboardActive(false);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  /**
   * 트리거로 포커스를 되돌리지 않고 닫기만 한다 — 바깥 클릭으로 닫을 때 쓴다.
   * 사용자가 검색 입력 같은 다른 요소를 클릭해서 닫힌 거라, `close()` 처럼 트리거로
   * 강제로 포커스를 되돌리면 방금 클릭한 곳의 포커스를 가로채 버린다.
   */
  const dismiss = () => setOpen(false);

  /** 칩 클릭·탭으로 여닫기 — 포인터 경로라 활성 옵션을 두지 않는다 */
  const toggle = () => (open ? close() : openListbox(false));

  // 활성 옵션이 스크롤 밖에 있으면 보이는 곳으로 당긴다 — 데스크톱은 7개라 거의 안 나지만
  // 모바일 시트는 좁은 화면에서 넘칠 수 있다
  useEffect(() => {
    if (!open || highlightedIndex === NO_ACTIVE_OPTION) return;

    listboxRef.current
      ?.querySelector<HTMLElement>(`[data-index="${highlightedIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, highlightedIndex]);

  const onTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key !== 'ArrowDown' &&
      event.key !== 'ArrowUp' &&
      event.key !== 'Enter' &&
      event.key !== ' '
    ) {
      return;
    }

    event.preventDefault();
    openListbox(true);
  };

  const onListboxKeyDown = (
    event: React.KeyboardEvent,
    onSelect: (index: number) => void,
  ) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        close();
        return;
      case 'Tab':
        // 포커스 이동은 브라우저 기본 동작에 맡기고 리스트박스만 접는다
        setOpen(false);
        return;
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        setKeyboardActive(true);
        const offset = event.key === 'ArrowDown' ? 1 : -1;
        setHighlightedIndex(index =>
          // 포인터로 열어 활성 옵션이 없는 상태였다면 선택된 항목에서 이동을 시작한다
          index === NO_ACTIVE_OPTION
            ? selectedIndex
            : (index + offset + optionCount) % optionCount,
        );
        return;
      }
      case 'Enter':
      case ' ':
        event.preventDefault();
        // 활성 옵션이 없으면 고를 대상도 없다 — `BLOG_CATEGORIES[-1]` 이 undefined 로
        // 흘러가 선택 값을 깨뜨리므로 여기서 끊는다(화살표로 활성 옵션을 먼저 만든다)
        if (highlightedIndex === NO_ACTIVE_OPTION) return;
        onSelect(highlightedIndex);
        close();
        return;
    }
  };

  return {
    open,
    hasOpened,
    highlightedIndex,
    keyboardActive,
    highlightByPointer,
    triggerRef,
    setListboxNode,
    toggle,
    close,
    dismiss,
    onTriggerKeyDown,
    onListboxKeyDown,
  };
}
